import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PicImg from "../PicImg";

/**
 * Product Gallery — main image + 6 thumbnail grid with active state.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Gallery")
 *  - heading (ReactNode)
 *  - subtitle
 *  - items: { src, caption }[]
 *  - seriesLabel: string (small orange caption label above active caption)
 *  - imageAlt: string
 */
export default function ProductGallery({
  testId,
  eyebrow = "Gallery",
  heading,
  subtitle,
  items = [],
  seriesLabel,
  imageAlt,
}) {
  const [activeImage, setActiveImage] = useState(items[0]?.src);

  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-cream-light py-24 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full pointer-events-none opacity-[0.14]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12 md:mb-14"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2 text-liftile-matte-black">{heading}</h2>
          {subtitle && (
            <p className="font-body text-liftile-matte-black/70 mt-5 text-base md:text-lg leading-relaxed">
              {subtitle}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="relative aspect-[16/9] md:aspect-[16/8] rounded-3xl overflow-hidden bg-liftile-matte-black shadow-[0_25px_70px_-25px_rgba(0,0,0,0.35)] border border-liftile-matte-black/[0.05]"
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={activeImage}
              src={activeImage}
              alt={imageAlt}
              data-testid={`${testId}-main`}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>

          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/75 via-black/25 to-transparent pointer-events-none" />
          <AnimatePresence mode="wait">
            <motion.div
              key={activeImage + "-caption"}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 0 }}
              transition={{ duration: 0.35, delay: 0.08 }}
              className="absolute bottom-6 left-6 md:bottom-8 md:left-8 max-w-md"
            >
              {seriesLabel && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-8 h-px bg-liftile-orange" />
                  <span className="font-heading text-[10px] uppercase tracking-[0.28em] text-liftile-orange font-semibold">
                    {seriesLabel}
                  </span>
                </div>
              )}
              <p className="font-heading text-sm md:text-base text-liftile-cream/95 font-medium leading-snug">
                {items.find((g) => g.src === activeImage)?.caption}
              </p>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          data-testid={`${testId}-thumbnails`}
          className="grid grid-cols-3 sm:grid-cols-6 gap-3 md:gap-4 mt-6 md:mt-7"
        >
          {items.map((g, i) => {
            const active = g.src === activeImage;
            return (
              <button
                key={g.src + i}
                type="button"
                onClick={() => setActiveImage(g.src)}
                data-testid={`${testId}-thumb-${i}`}
                className={`group relative aspect-[4/3] rounded-xl overflow-hidden transition-all duration-400 ${
                  active
                    ? "ring-2 ring-liftile-orange shadow-[0_10px_25px_-8px_rgba(255,107,26,0.5)]"
                    : "ring-1 ring-liftile-matte-black/10 hover:ring-liftile-orange/60 hover:-translate-y-0.5"
                }`}
              >
                <PicImg
                  src={g.src}
                  alt={g.caption}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
                <span
                  className={`absolute inset-0 transition-colors duration-400 ${
                    active
                      ? "bg-liftile-orange/0"
                      : "bg-liftile-matte-black/25 group-hover:bg-transparent"
                  }`}
                />
              </button>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
